import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useToken } from '@/common/helper';
import {
  createSavedDateList,
  deleteSavedDateList,
  getSavedDateList,
  listSavedDateLists,
  listSystemDatePresets,
  updateSavedDateList,
  type ListSystemDatePresetsResult,
  type SavedDateList,
  type SavedDateListInput,
} from '@/apis/logiblocksAPI';

/** Every condition's value field mounts these queries; a short staleTime
 * stops a canvas full of conditions from refetching on each mount. */
const STALE_TIME_MS = 30_000;

/** React Query keys for the date-set value menu's server data. */
export const dateSetKeys = {
  all: ['date-sets'] as const,
  presets: () => [...dateSetKeys.all, 'presets'] as const,
  lists: () => [...dateSetKeys.all, 'lists'] as const,
  list: (listId: string) => [...dateSetKeys.lists(), listId] as const,
};

/** Reads the live token at call time (the `token` state can lag a silent
 * refresh) and gates a query on being logged in. */
function useQueryToken() {
  const { token, checkTokenExpiry } = useToken();
  const requireToken = (): string => {
    const liveToken = checkTokenExpiry();
    if (!liveToken) throw new Error('No token available');
    return liveToken;
  };
  return { enabled: !!token, requireToken };
}

/** The Zoomlogi system presets plus the company's default timezone. */
export function useSystemDatePresets(): UseQueryResult<ListSystemDatePresetsResult> {
  const { enabled, requireToken } = useQueryToken();
  return useQuery({
    queryKey: dateSetKeys.presets(),
    queryFn: () => listSystemDatePresets(requireToken()),
    enabled,
    staleTime: STALE_TIME_MS,
  });
}

/** All of the company's saved date/day lists. */
export function useSavedDateLists(): UseQueryResult<SavedDateList[]> {
  const { enabled, requireToken } = useQueryToken();
  return useQuery({
    queryKey: dateSetKeys.lists(),
    queryFn: () => listSavedDateLists(requireToken()),
    enabled,
    staleTime: STALE_TIME_MS,
  });
}

/** One saved date/day list, for the list-edit panel. */
export function useSavedDateList(
  listId: string
): UseQueryResult<SavedDateList> {
  const { enabled, requireToken } = useQueryToken();
  return useQuery({
    queryKey: dateSetKeys.list(listId),
    queryFn: () => getSavedDateList(requireToken(), listId),
    enabled,
    staleTime: STALE_TIME_MS,
  });
}

/** Creates a saved list, then refreshes every saved-list query. */
export function useCreateSavedDateList(): UseMutationResult<
  SavedDateList,
  Error,
  SavedDateListInput
> {
  const queryClient = useQueryClient();
  const { requireToken } = useQueryToken();
  return useMutation({
    mutationFn: (input: SavedDateListInput) =>
      createSavedDateList(requireToken(), input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: dateSetKeys.lists() }),
  });
}

/** Updates a saved list, then refreshes every saved-list query. */
export function useUpdateSavedDateList(
  listId: string
): UseMutationResult<SavedDateList, Error, SavedDateListInput> {
  const queryClient = useQueryClient();
  const { requireToken } = useQueryToken();
  return useMutation({
    mutationFn: (input: SavedDateListInput) =>
      updateSavedDateList(requireToken(), listId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: dateSetKeys.lists() }),
  });
}

/** Deletes a saved list, then refreshes every other saved-list query. The
 * deleted list's own query is left alone: its edit panel is still mounted until
 * the delete resolves, and a refetch would 400 "not found". */
export function useDeleteSavedDateList(
  listId: string
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  const { requireToken } = useQueryToken();
  return useMutation({
    mutationFn: () => deleteSavedDateList(requireToken(), listId),
    onSuccess: () => {
      const deletedHash = hashKey(dateSetKeys.list(listId));
      return queryClient.invalidateQueries({
        queryKey: dateSetKeys.lists(),
        predicate: (query) => query.queryHash !== deletedHash,
      });
    },
  });
}
